import 'froala-editor/js/froala_editor.pkgd.min';
import 'froala-editor/js/third_party/embedly.min';
import 'froala-editor/js/plugins/font_family.min';
import 'froala-editor/js/plugins/font_size.min';
import 'froala-editor/js/plugins/colors.min';
import 'froala-editor/js/plugins/link.min';
import 'froala-editor/js/plugins/code_view.min';
import 'froala-editor/js/plugins/lists.min';
import 'froala-editor/js/plugins/image.min';
import 'froala-editor/js/plugins/line_breaker.min';
import 'froala-editor/js/plugins/table.min';
import 'froala-editor/js/plugins/align.min';
import 'froala-editor/js/third_party/font_awesome.min';
import 'froala-editor/js/plugins/emoticons.min';
import 'froala-editor/css/plugins/emoticons.min.css';
import '@fortawesome/fontawesome-pro/css/all.min.css';
import '@fortawesome/fontawesome-pro/css/v4-shims.min.css';
import '@fortawesome/fontawesome-pro/js/all.min';
import '@fortawesome/fontawesome-pro/js/v4-shims.min';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { includes, eq, set, get, startCase, camelCase, find } from 'lodash';
import FroalaEditor from 'react-froala-wysiwyg';
import { useQuery, useMutation } from '@apollo/client';
import { str as Crc32 } from 'crc-32';
import {
  PAGE_DATA,
  GENERAL_SETTINGS,
  CLIENT_STATE,
  UPDATE_PAGE_DATA,
  UPDATE_GENERAL_SETTINGS,
  SET_CLIENT_STATE,
  PAGE_LIST,
} from '../../../graphqls';
import onImageBeforeUpload from '../../../actions/onImageBeforeUpload';
import onBlur from '../../../actions/onBlur';

export default function TextEditor() {
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  const {
    loading: loadingClientState,
    data: clientState,
    data: { modalTypeShown, selectedPlanNodeId, selectedSubContentIndex, selectedIntroContentIndex } = {},
  } = useQuery(CLIENT_STATE);
  const {
    loading: loadingPageData,
    data: {
      pageData,
      pageData: {
        cache: {
          banner,
          introContent,
          children,
          subContent,
          description,
          latestUpdate,
          title,
          comment,
          measures,
          additionalText,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          measureId,
          planNodeId,
          __typename: pageDataType,
          parentPlanNodeId = 'home',
        } = {},
      } = {},
    } = {},
  } = useQuery(PAGE_DATA, { variables: { name: eq(paths[1], 'home') ? 'home' : paths[2] } });
  const { loading: loadingGeneralSettings, data: { generalSettings, generalSettings: { footer, fontFamily } = {} } = {} } =
    useQuery(GENERAL_SETTINGS);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const [updatePageData] = useMutation(UPDATE_PAGE_DATA);
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, { refetchQueries: [{ query: GENERAL_SETTINGS }] });
  const [pageType, setPageType] = useState('');
  const [textType, setTextType] = useState('');
  const [model, setModel] = useState('');
  const [planNodeName, setPlanNodeName] = useState('');
  const defaultFontFamily = startCase(camelCase(fontFamily));

  useEffect(() => {
    setPageType(pageDataType);

    switch (modalTypeShown) {
      case 'bannerText':
        setTextType('banner.text');
        setModel(banner.text);
        break;
      case 'introText':
        setTextType(`[${selectedIntroContentIndex}].text`);
        setModel(get(introContent, [selectedIntroContentIndex, 'text']));
        break;
      case 'featureText':
        setTextType(selectedPlanNodeId);
        setPlanNodeName(get(find(children, ['planNodeId', selectedPlanNodeId]), 'title'));
        setModel(get(find(children, ['planNodeId', selectedPlanNodeId]), 'description'));
        break;
      case 'subText':
        setTextType(`[${selectedSubContentIndex}].text`);
        setModel(get(subContent, [selectedSubContentIndex, 'text']));
        break;
      case 'footerText':
        setTextType('footerText');
        setModel(footer);
        break;
      case 'description':
        setTextType('description');
        setModel(description);
        break;
      case 'latestUpdate':
        setTextType('latestUpdate');
        setModel(latestUpdate);
        break;
      case 'additionalText':
        setTextType('additionalText');
        if (additionalText) {
          setModel(additionalText);
        }
        break;
      case 'additionalBottomText':
        setTextType('additionalBottomText');
        if (additionalBottomText) {
          setModel(additionalBottomText);
        }
        break;
      case 'additionalMiddleText':
        setTextType('additionalMiddleText');
        if (additionalMiddleText) {
          setModel(additionalMiddleText);
        }
        break;
      case 'additionalUpperMiddleText':
        setTextType('additionalUpperMiddleText');
        if (additionalUpperMiddleText) {
          setModel(additionalUpperMiddleText);
        }
        break;
      case 'measureDetail':
        setTextType('measureText');
        setPlanNodeName(title);
        setModel(description);
        break;
      case 'comment':
        setTextType('measureComment');
        setModel(comment);
        break;
      case 'visualText':
        setTextType(selectedPlanNodeId);
        setPlanNodeName(get(find(measures, ['measureId', selectedPlanNodeId]), 'visualTitle'));
        setModel(get(find(measures, ['measureId', selectedPlanNodeId]), 'visualDescription'));
        break;
      default:
        break;
    }
  }, [clientState, pageData, generalSettings]);

  if (loadingClientState || loadingGeneralSettings || loadingPageData) {
    return '';
  }

  const onModelChange = (modelValue) => setModel(modelValue);
  const onChange = ({ target: { value } }) => setPlanNodeName(value);
  const onSubmit = () => {
    switch (pageType) {
      case 'Homepage':
        switch (modalTypeShown) {
          case 'featureText':
            updatePageData({
              variables: {
                name: selectedPlanNodeId,
                cache: {
                  title: planNodeName,
                  description: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  planNodeId: selectedPlanNodeId,
                },
                itemId: selectedPlanNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: parentPlanNodeId || 'home' } }, { query: PAGE_LIST }],
            });
            break;
          case 'subText':
            updatePageData({
              variables: {
                cache: {
                  subContent: set(JSON.parse(JSON.stringify(subContent)), textType, model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3')),
                },
              },
            });
            break;
          case 'introText':
            updatePageData({
              variables: {
                cache: {
                  introContent: set(
                    JSON.parse(JSON.stringify(introContent)),
                    textType,
                    model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  ),
                },
              },
            });
            break;
          case 'footerText':
            updateGeneralSettings({ variables: { footer: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') } });
            break;
          default:
            updatePageData({ variables: { cache: { ...set({}, textType, model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3')) } } });
            break;
        }
        break;
      case 'PlanNodePage':
        switch (modalTypeShown) {
          case 'latestUpdate':
            updatePageData({
              variables: {
                name: planNodeId,
                cache: { latestUpdate: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'), planNodeId },
                itemId: planNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: planNodeId } }],
            });
            break;
          case 'additionalText':
            updatePageData({
              variables: {
                name: planNodeId,
                cache: { additionalText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'), planNodeId },
                itemId: planNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId || planNodeId } }],
            });
            break;
          case 'additionalBottomText':
            updatePageData({
              variables: {
                name: planNodeId,
                cache: {
                  additionalBottomText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  planNodeId,
                },
                itemId: planNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId || planNodeId } }],
            });
            break;
          case 'additionalMiddleText':
            updatePageData({
              variables: {
                name: planNodeId,
                cache: {
                  additionalMiddleText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  planNodeId,
                },
                itemId: planNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId || planNodeId } }],
            });
            break;
          case 'additionalUpperMiddleText':
            updatePageData({
              variables: {
                name: planNodeId,
                cache: {
                  additionalUpperMiddleText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  planNodeId,
                },
                itemId: planNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId || planNodeId } }],
            });
            break;
          case 'visualText':
            updatePageData({
              variables: {
                name: selectedPlanNodeId,
                cache: {
                  visualTitle: planNodeName,
                  visualDescription: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  measureId: selectedPlanNodeId,
                },
                itemId: selectedPlanNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: planNodeId } }],
            });
            break;
          default:
            updatePageData({
              variables: {
                name: selectedPlanNodeId,
                cache: {
                  title: planNodeName,
                  description: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3'),
                  planNodeId: selectedPlanNodeId,
                },
                itemId: selectedPlanNodeId,
              },
              refetchQueries: [{ query: PAGE_DATA, variables: { name: planNodeId } }],
            });
            break;
        }
        break;
      case 'MeasurePage':
        if (eq(modalTypeShown, 'measureDetail')) {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { title: planNodeName, description: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        } else if (eq(modalTypeShown, 'additionalText')) {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { additionalText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        } else if (eq(modalTypeShown, 'additionalBottomText')) {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { additionalBottomText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        } else if (eq(modalTypeShown, 'additionalMiddleText')) {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { additionalMiddleText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        } else if (eq(modalTypeShown, 'additionalUpperMiddleText')) {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { additionalUpperMiddleText: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        } else {
          updatePageData({
            variables: {
              name: measureId,
              itemId: measureId,
              cache: { comment: model.replace(/(>[^<']*)('+)(.*<)/gm, '$1&apos;$3') },
            },
            refetchQueries: [{ query: PAGE_DATA, variables: { name: measureId } }],
          });
        }
        break;
      default:
        break;
    }

    setState({ variables: { modalTypeShown: '' } });
  };
  const config = {
    zIndex: 1051,
    attribution: false,
    charCounterCount: false,
    disableRightClick: false,
    heightMin: 150,
    heightMax: 300,
    htmlExecuteScripts: false,
    imageAltButtons: ['imageBack'],
    htmlAllowedTags: [
      'a',
      'abbr',
      'address',
      'area',
      'article',
      'aside',
      'audio',
      'b',
      'base',
      'bdi',
      'bdo',
      'blockquote',
      'br',
      'button',
      'canvas',
      'caption',
      'cite',
      'code',
      'col',
      'colgroup',
      'datalist',
      'dd',
      'del',
      'details',
      'dfn',
      'dialog',
      'div',
      'dl',
      'dt',
      'em',
      'embed',
      'fieldset',
      'figcaption',
      'figure',
      'footer',
      'form',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'header',
      'hgroup',
      'hr',
      'i',
      'iframe',
      'img',
      'input',
      'ins',
      'kbd',
      'keygen',
      'label',
      'legend',
      'li',
      'link',
      'main',
      'map',
      'mark',
      'menu',
      'menuitem',
      'meter',
      'nav',
      'noscript',
      'object',
      'ol',
      'optgroup',
      'option',
      'output',
      'p',
      'param',
      'path',
      'pre',
      'progress',
      'queue',
      'rp',
      'rt',
      'ruby',
      's',
      'samp',
      'script',
      'style',
      'section',
      'select',
      'small',
      'source',
      'span',
      'strike',
      'strong',
      'sub',
      'summary',
      'sup',
      'svg',
      'table',
      'tbody',
      'td',
      'textarea',
      'tfoot',
      'th',
      'thead',
      'time',
      'title',
      'tr',
      'track',
      'u',
      'ul',
      'var',
      'video',
      'wbr',
    ],
    htmlAllowedAttrs: [
      'accept',
      'accept-charset',
      'accesskey',
      'action',
      'align',
      'allowfullscreen',
      'allowtransparency',
      'alt',
      'aria-.*',
      'async',
      'autocomplete',
      'autofocus',
      'autoplay',
      'autosave',
      'background',
      'bgcolor',
      'border',
      'charset',
      'cellpadding',
      'cellspacing',
      'checked',
      'cite',
      'class',
      'color',
      'cols',
      'colspan',
      'content',
      'contenteditable',
      'contextmenu',
      'controls',
      'coords',
      'd',
      'data',
      'data-.*',
      'datetime',
      'default',
      'defer',
      'dir',
      'dirname',
      'disabled',
      'download',
      'draggable',
      'dropzone',
      'enctype',
      'fill',
      'focusable',
      'for',
      'form',
      'formaction',
      'frameborder',
      'headers',
      'height',
      'hidden',
      'high',
      'href',
      'hreflang',
      'http-equiv',
      'icon',
      'id',
      'ismap',
      'itemprop',
      'keytype',
      'kind',
      'label',
      'lang',
      'language',
      'list',
      'loop',
      'low',
      'max',
      'maxlength',
      'media',
      'method',
      'min',
      'mozallowfullscreen',
      'multiple',
      'muted',
      'name',
      'novalidate',
      'open',
      'optimum',
      'pattern',
      'ping',
      'placeholder',
      'playsinline',
      'poster',
      'preload',
      'pubdate',
      'radiogroup',
      'readonly',
      'rel',
      'required',
      'reversed',
      'role',
      'rows',
      'rowspan',
      'sandbox',
      'scope',
      'scoped',
      'scrolling',
      'seamless',
      'selected',
      'shape',
      'size',
      'sizes',
      'span',
      'src',
      'srcdoc',
      'srclang',
      'srcset',
      'start',
      'step',
      'summary',
      'spellcheck',
      'style',
      'tabindex',
      'target',
      'title',
      'type',
      'translate',
      'usemap',
      'value',
      'valign',
      'viewBox',
      'webkitallowfullscreen',
      'width',
      'wrap',
      'xmlns',
    ],
    pastePlain: true,
    fontFamily: {
      Arial: 'Arial',
      'Crimson Text': 'Crimson Text',
      Georgia: 'Georgia',
      Lato: 'Lato',
      Montserrat: 'Montserrat',
      'Nunito Sans': 'Nunito Sans',
      'Open Sans': 'Open Sans',
      Oswald: 'Oswald',
      Poppins: 'Poppins',
      Raleway: 'Raleway',
      Roboto: 'Roboto',
      'Source Sans Pro': 'Source Sans Pro',
      'PT Serif': 'PT Serif',
      Tahoma: 'Tahoma',
      'Times New Roman': 'Times New Roman',
      Verdana: 'Verdana',
    },
    fontFamilyDefaultSelection: defaultFontFamily,
    editorClass: 'fr-envisio',
    fontSize: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '30', '36', '48', '60', '72', '96'],
    key: 'gVG3C-8F2F1C2D4A3B2ud1BI1IMNBUMRWAi1AYMSTRBUZYB-16D4E3D2F3C3I2I1B10A2D2==',
    toolbarButtons: [
      'bold',
      'italic',
      'underline',
      'fontFamily',
      'fontSize',
      'textColor',
      'backgroundColor',
      'clearFormatting',
      'formatOL',
      'formatUL',
      'align',
      'insertLink',
      'insertImage',
      'embedly',
      'emoticons',
      'fontAwesome',
      'undo',
      'redo',
      'insertHR',
      'insertTable',
      'html',
    ],
    emoticonsUseImage: false,
    imagePaste: false,
    linkAlwaysBlank: true,
    iconsTemplate: 'font_awesome_5l',
    fontAwesomeTemplate: '<i class="fa fa-[NAME] fr-deletable" aria-hidden="true">&nbsp;</i>',
    // fontAwesomeSets: [{
    //   title: 'Icons',
    //   icon: 'thumbs-up',
    //   list: ['angle-down',
    //     'angle-left',
    //     'angle-right',
    //     'angle-up',
    //     'arrow-alt-down',
    //     'arrow-alt-up',
    //     'arrow-circle-down',
    //     'arrow-circle-right',
    //     'arrow-circle-up',
    //     'arrow-circle-left',
    //     'arrow-up',
    //     'arrow-down',
    //     'arrow-left',
    //     'arrow-right',
    //     'arrows-h',
    //     'award',
    //     'balance-scale',
    //     'balance-scale-left',
    //     'balance-scale-right',
    //     'bell',
    //     'bell-exclamation',
    //     'biking',
    //     'car',
    //     'caret-down',
    //     'caret-left',
    //     'caret-right',
    //     'caret-up',
    //     'check',
    //     'check-circle',
    //     'check-square',
    //     'circle',
    //     'thumbs-up',
    //     'thumbs-down',
    //     'user-graduate',
    //     'graduation-cap',
    //     'theater-masks'],
    // }],
    tableStyles: { 'fr-dashed-borders': 'Dashed Borders', 'fr-alternate-rows': 'Alternate Rows', 'fr-table-border': 'Table Border' },
    pluginsEnabled: [
      'fontFamily',
      'fontSize',
      'colors',
      'link',
      'codeView',
      'lists',
      'image',
      'lineBreaker',
      'embedly',
      'table',
      'align',
      'emoticons',
      'fontAwesome',
    ],
    events: { 'image.beforeUpload': onImageBeforeUpload, blur: onBlur },
    imageUploadRemoteUrls: false,
    imageDefaultWidth: 0,
  };

  return (
    <>
      {includes(['featureText', 'measureDetail', 'visualText'], modalTypeShown) ? (
        <div className="form-group">
          <label htmlFor="planNodeTitle">
            {includes(['measureDetail', 'featureText'], modalTypeShown) ? 'Navigation Label' : 'Title'}
            <input
              className="form-control"
              name="planNodeTitle"
              id="planNodeTitle"
              type="text"
              value={planNodeName}
              onChange={onChange}
              required
            />
          </label>
        </div>
      ) : (
        ''
      )}
      <style>
        {`
          .fr-envisio .pd-custom {
            font-family: "${defaultFontFamily}"!important;
          }
        `}
      </style>
      <FroalaEditor model={model} onModelChange={onModelChange} config={config} />
      {modalTypeShown === 'visualText' ? (
        <div className="unique-class">
          <span className="unique-class__label">Class name:&nbsp;</span>
          <span className="unique-class__name">{`Measure-${Math.abs(Crc32(selectedPlanNodeId))}`}</span>
        </div>
      ) : (
        ''
      )}
      <div className="button-container">
        <button type="button" className="btn mint-btn" onClick={onSubmit}>
          Submit
        </button>
      </div>
    </>
  );
}
